import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Receipt } from "lucide-react";
import { adminAuthHeader } from "@/hooks/useAdminAuth";
import { extractApiError } from "@/utils/apiError";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatEur(n) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n) || 0);
}
function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

/**
 * Admin panel to mark manual purchases on a lead (off-Stripe — bank transfer,
 * cash, in-kind) and to delete test entries. Independent from real `transactions`.
 */
export default function PurchaseMarkersPanel({ userId, markers = [], onChanged }) {
  const [showAdd, setShowAdd] = useState(false);
  const [amount, setAmount] = useState("");
  const [label, setLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const total = markers.reduce((s, m) => s + (Number(m.amount_eur) || 0), 0);

  const reset = () => { setAmount(""); setLabel(""); setShowAdd(false); };

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    const amt = parseFloat(amount.replace(",", "."));
    if (!isFinite(amt) || amt < 0) {
      toast.error("Importo non valido");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/admin/users/${userId}/purchase-markers`,
        { amount_eur: amt, label },
        { headers: adminAuthHeader() },
      );
      toast.success(`Acquisto segnalato (${formatEur(amt)})`);
      reset();
      onChanged?.();
    } catch (err) {
      toast.error(extractApiError(err, "Errore nel salvataggio"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (markerId) => {
    if (!window.confirm("Eliminare questo acquisto segnalato?")) return;
    try {
      await axios.delete(
        `${API}/admin/users/${userId}/purchase-markers/${markerId}`,
        { headers: adminAuthHeader() },
      );
      toast.success("Acquisto eliminato");
      onChanged?.();
    } catch (err) {
      toast.error(extractApiError(err, "Errore nell'eliminazione"));
    }
  };

  return (
    <div
      className="rounded-2xl p-5 border"
      style={{ borderColor: "#dbe3ec", background: "#fff" }}
      data-testid="purchase-markers-panel"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <Receipt size={16} style={{ color: "var(--np-secondary)" }} />
          <h3 className="text-[13px] font-semibold tracking-[0.16em] uppercase" style={{ color: "var(--np-primary)" }}>
            Acquisti segnalati ({markers.length})
          </h3>
          {markers.length > 0 && (
            <span className="ml-2 text-[12px] font-semibold tabular-nums px-2.5 py-0.5 rounded-full" style={{ background: "#ecfdf5", color: "#065f46" }}>
              Totale {formatEur(total)}
            </span>
          )}
        </div>
        {!showAdd && (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            data-testid="add-purchase-marker-btn"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border transition"
            style={{ background: "#fff7ed", borderColor: "#fdba74", color: "#9a3412" }}
          >
            <Plus size={13} /> Segnala acquisto
          </button>
        )}
      </div>

      {showAdd && (
        <form
          onSubmit={handleAdd}
          className="mb-4 grid gap-2 sm:grid-cols-[140px_1fr_auto_auto] items-stretch p-3 rounded-lg"
          style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}
          data-testid="add-purchase-marker-form"
        >
          <input
            type="text"
            inputMode="decimal"
            placeholder="€ Importo"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="purchase-marker-amount"
            autoFocus
            required
            className="h-10 px-3 rounded-md border text-sm tabular-nums"
            style={{ borderColor: "#cbd5e1" }}
          />
          <input
            type="text"
            placeholder="Descrizione (es. Corso completo, Bundle, Bonifico…)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            data-testid="purchase-marker-label"
            maxLength={120}
            className="h-10 px-3 rounded-md border text-sm"
            style={{ borderColor: "#cbd5e1" }}
          />
          <button
            type="submit"
            disabled={submitting}
            data-testid="purchase-marker-save-btn"
            className="h-10 px-4 rounded-md text-xs font-semibold text-white inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
            style={{ background: "var(--np-secondary)" }}
          >
            {submitting ? <Loader2 size={13} className="animate-spin" /> : null} Salva
          </button>
          <button
            type="button"
            onClick={reset}
            className="h-10 px-3 rounded-md text-xs font-semibold border"
            style={{ borderColor: "#cbd5e1", color: "#475569", background: "#fff" }}
          >
            Annulla
          </button>
        </form>
      )}

      {markers.length === 0 ? (
        <p className="text-sm italic" style={{ color: "var(--np-muted)" }}>
          Nessun acquisto segnalato. Usa il bottone in alto per registrare una vendita extra-Stripe (bonifico, contanti…).
        </p>
      ) : (
        <ul className="divide-y" style={{ borderColor: "#eef1f5" }}>
          {markers.map((m) => (
            <li key={m.id} className="py-3 flex items-center justify-between gap-3" data-testid={`purchase-marker-row-${m.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="text-base font-semibold tabular-nums" style={{ color: "var(--np-primary)" }}>
                    {formatEur(m.amount_eur)}
                  </span>
                  {m.label && <span className="text-sm truncate" style={{ color: "#334155" }}>{m.label}</span>}
                </div>
                <p className="text-[11px] mt-0.5" style={{ color: "var(--np-muted)" }}>
                  Segnalato il {formatDate(m.created_at)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleDelete(m.id)}
                data-testid={`purchase-marker-delete-${m.id}`}
                className="p-2 rounded-md transition"
                style={{ color: "#b91c1c" }}
                title="Elimina questo acquisto"
              >
                <Trash2 size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
